// Libraries
import { createAsyncThunk, createSlice, PayloadAction } from '@reduxjs/toolkit';

// Store
import { RootState } from '../../app/store';

// API
import { getToken, getGameMods } from './appApi';

// Constants
const apiToken = localStorage.getItem('apiToken')
    ? localStorage.getItem('apiToken')
    : null;

/**
 * @interface AppState
 * Describes the properties for the global app state
 *
 * @property {boolean} apiToken Current token to authenticate API requests
 * @property {boolean} menuOpen Whether or not the settings menu is open
 * @property {boolean} playersListOpen Whether or not the players list is open
 * @property {string} modalContent Contents of modal dialog
 * @property {string} splashMessage Content of splash message dialog
 * @property {string} gameMods Available game modifications
 * @property {string} status Data fetching status
 * @property {string} error Data fetching error message
 */
export interface AppState {
    apiToken: string | null;
    menuOpen: boolean;
    playersListOpen: boolean;
    modalContent: string;
    splashMessage: string;
    gameMods: {
        id: number;
        type: string;
        name: string;
        slug: string;
    }[];
    status: 'idle' | 'loading' | 'failed';
    error: string;
}

/**
 * @const initialState
 * Describes the initial app state on application start up
 */
const initialState: AppState = {
    apiToken: apiToken,
    menuOpen: false,
    playersListOpen: false,
    modalContent: '',
    splashMessage: '',
    gameMods: [],
    status: 'idle',
    error: '',
};

/**
 * @function getTokenAsync
 * Fetches an API token for use with the application
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @return {string} The API token
 */
export const getTokenAsync = createAsyncThunk<
    { token: string },
    void,
    { rejectValue: string }
>('app/getToken', async (_, { rejectWithValue }) => {
    try {
        const response = await getToken();
        return response;
    } catch (error: any) {
        return rejectWithValue(error);
    }
});

/**
 * @function getGameModsAsync
 * Fetches the current list of available game mods
 * https://redux-toolkit.js.org/api/createAsyncThunk
 *
 * @return {array} The array of game mod objects
 */
export const getGameModsAsync = createAsyncThunk(
    'app/getGameMods',
    async () => {
        const response = await getGameMods();
        return response['hydra:member'];
    },
);

/**
 * @const appSlice
 * Describes initial state and reducer functions to handle app state changes
 * https://redux-toolkit.js.org/api/createslice
 */
export const appSlice = createSlice({
    name: 'app',
    initialState,
    reducers: {
        pushMenuStatus: (state, action: PayloadAction<boolean>) => {
            state.menuOpen = action.payload;
        },
        pushPlayersListStatus: (state, action: PayloadAction<boolean>) => {
            state.playersListOpen = action.payload;
        },
        pushModalContent: (state, action: PayloadAction<string>) => {
            state.modalContent = action.payload;
        },
        pushSplashMessage: (state, action: PayloadAction<string>) => {
            state.splashMessage = action.payload;
        },
        clearApiToken: (state) => {
            state.apiToken = null;
        },
        clearModalContent: (state) => {
            state.modalContent = '';
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(getTokenAsync.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(getTokenAsync.fulfilled, (state, action) => {
                state.apiToken = action.payload.token;
                state.status = 'idle';
                state.error = '';
            })
            .addCase(getTokenAsync.rejected, (state, action) => {
                state.status = 'failed';
                state.error = action.payload
                    ? action.payload
                    : 'We are currently experiencing issues';
            })
            .addCase(getGameModsAsync.pending, (state) => {
                state.status = 'loading';
                state.error = '';
            })
            .addCase(getGameModsAsync.fulfilled, (state, action) => {
                state.gameMods = action.payload;
                state.status = 'idle';
                state.error = '';
            })
            .addCase(getGameModsAsync.rejected, (state) => {
                state.status = 'failed';
            });
    },
});

/**
 * @const selectApiToken
 * Exposes apiToken property in app state for use in rest of application
 */
export const selectApiToken = (state: RootState) => state.app.apiToken;

/**
 * @const selectAppStatus
 * Exposes current fetch status of app data for use in rest of application
 */
export const selectAppStatus = (state: RootState) => state.app.status;

/**
 * @const selectAppError
 * Exposes current fetch error of app data for use in rest of application
 */
export const selectAppError = (state: RootState) => state.app.error;

/**
 * @const selectMenuStatus
 * Exposes menuOpen property in app state for use in rest of application
 */
export const selectMenuStatus = (state: RootState) => state.app.menuOpen;

/**
 * @const selectPlayersListStatus
 * Exposes playersListOpen property in app state for use in rest of application
 */
export const selectPlayersListStatus = (state: RootState) =>
    state.app.playersListOpen;

/**
 * @const selectModalContent
 * Exposes modalContent property in app state for use in rest of application
 */
export const selectModalContent = (state: RootState) => state.app.modalContent;

/**
 * @const selectSplashMessage
 * Exposes splashMessage property in app state for use in rest of application
 */
export const selectSplashMessage = (state: RootState) =>
    state.app.splashMessage;

/**
 * @const selectGameMods
 * Exposes gameMods property in app state for use in rest of application
 */
export const selectGameMods = (state: RootState) => state.app.gameMods;

/**
 * @const pushMenuStatus
 * Exposes pushMenuStatus action for use in rest of application
 */
export const { pushMenuStatus } = appSlice.actions;

/**
 * @const pushPlayersListStatus
 * Exposes pushPlayersListStatus action for use in rest of application
 */
export const { pushPlayersListStatus } = appSlice.actions;

/**
 * @const pushModalContent
 * Exposes pushModalContent action for use in rest of application
 */
export const { pushModalContent } = appSlice.actions;

/**
 * @const pushSplashMessage
 * Exposes pushSplashMessage action for use in rest of application
 */
export const { pushSplashMessage } = appSlice.actions;

/**
 * @const clearApiToken
 * Exposes clearApiToken action for use in rest of application
 */
export const { clearApiToken } = appSlice.actions;

/**
 * @const clearModalContent
 * Exposes clearModalContent action for use in rest of application
 */
export const { clearModalContent } = appSlice.actions;

/**
 * @const appSlice.reducer
 * Exposes app reducer for use in store
 */
export default appSlice.reducer;
